import AsyncStorage from '@react-native-async-storage/async-storage';

/**
 * Cache offline simple basado en AsyncStorage con TTL.
 *
 * Uso típico:
 *   await offlineCache.set('restaurants', items);
 *   const cached = await offlineCache.get('restaurants');
 *
 * Si el TTL expiró, devuelve null. Por defecto 24 horas.
 *
 * No es un "service worker" verdadero — es un fallback para que la app
 * abra mostrando algo cuando no hay internet, mientras llega la data fresca
 * en background.
 */

const PREFIX = 'gs_cache_';
const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000; // 24h

interface CachedEntry<T> {
  ts: number;
  ttlMs: number;
  data: T;
}

export const offlineCache = {
  async set<T>(key: string, data: T, ttlMs: number = DEFAULT_TTL_MS): Promise<void> {
    try {
      const entry: CachedEntry<T> = { ts: Date.now(), ttlMs, data };
      await AsyncStorage.setItem(PREFIX + key, JSON.stringify(entry));
    } catch (e) {
      console.warn('[offlineCache.set] falló:', e);
    }
  },

  async get<T>(key: string): Promise<T | null> {
    try {
      const raw = await AsyncStorage.getItem(PREFIX + key);
      if (!raw) return null;
      const entry: CachedEntry<T> = JSON.parse(raw);
      if (Date.now() - entry.ts > entry.ttlMs) {
        // expired
        AsyncStorage.removeItem(PREFIX + key).catch(() => {});
        return null;
      }
      return entry.data;
    } catch (e) {
      console.warn('[offlineCache.get] falló:', e);
      return null;
    }
  },

  async clear(key: string): Promise<void> {
    try {
      await AsyncStorage.removeItem(PREFIX + key);
    } catch {
      // silent
    }
  },

  async clearAll(): Promise<void> {
    try {
      const keys = await AsyncStorage.getAllKeys();
      const ours = keys.filter((k) => k.startsWith(PREFIX));
      if (ours.length) await AsyncStorage.multiRemove(ours);
    } catch {
      // silent
    }
  },
};
